import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { ArrowBack, Print, Close } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const ReportView = ({ report: propReport, onClose, isDialog = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(propReport);
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(!propReport);
  const [error, setError] = useState('');

  const fetchReport = useCallback(async (reportId) => {
    try {
      const [reportResponse, resultsResponse] = await Promise.all([
        axios.get(`${API_URL}/api/reports/${reportId}`),
        axios.get(`${API_URL}/api/reports/${reportId}/results`)
      ]);
      setReport(reportResponse.data);
      setTestResults(resultsResponse.data);
    } catch (err) {
      setError('Failed to load report');
      console.error('Error fetching report:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (propReport) {
      setReport(propReport);
      if (propReport.id) {
        fetchTestResults(propReport.id);
      }
    } else if (id) {
      fetchReport(id);
    }
  }, [propReport, id, fetchReport]);

  const fetchTestResults = async (reportId) => {
    try {
      const response = await axios.get(`${API_URL}/api/reports/${reportId}/results`);
      setTestResults(response.data);
    } catch (err) {
      console.error('Error fetching test results:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-GB') + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  // Helper to check abnormal values
  const isAbnormal = (value, refRange) => {
    if (!value || !refRange) return null;
    const match = refRange.match(/([\d.]+)\s*-\s*([\d.]+)/);
    if (match) {
      const min = parseFloat(match[1]);
      const max = parseFloat(match[2]);
      const val = parseFloat(value);
      if (!isNaN(val)) {
        if (val < min) return 'L';
        if (val > max) return 'H';
      }
    }
    return null;
  };

  // Group test results by category
  const groupedResults = testResults.reduce((acc, result) => {
    const category = result.test?.testCategory?.name || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(result);
    return acc;
  }, {});

  // Calculate age
  const getAge = (dob) => {
    if (!dob) return '';
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handlePrint = () => {
    if (!report) return;
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Generate the professional HTML format
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Report #${report.id}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; color: #333; }
          .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #ddd; padding-bottom: 20px; margin-bottom: 20px; }
          .header-left h2 { margin: 0; font-size: 24px; font-weight: bold; }
          .header-left p { margin: 5px 0; font-size: 14px; }
          .header-right { text-align: right; }
          .qr-code { width: 64px; height: 64px; background: #f0f0f0; border: 1px solid #ddd; }
          .patient-info { display: grid; grid-template-columns: 2fr 1fr; gap: 20px; margin-bottom: 20px; }
          .patient-left p, .patient-right p { margin: 5px 0; font-size: 14px; }
          .test-category { margin: 20px 0; }
          .test-category h3 { margin: 10px 0; font-size: 18px; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 14px; }
          th { background: #f5f5f5; font-weight: bold; }
          .abnormal { color: #d32f2f; font-weight: bold; }
          .abnormal-high { color: #f57c00; font-weight: bold; }
          .clinical-notes, .possible-causes, .interpretation { margin: 20px 0; }
          .clinical-notes h4, .possible-causes h4, .interpretation h4 { margin: 10px 0; font-size: 16px; font-weight: bold; }
          .footer { display: flex; justify-content: space-between; align-items: flex-start; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; }
          .footer-left, .footer-right { font-size: 14px; }
          .footer-right { text-align: right; }
          .disclaimer { text-align: center; margin-top: 20px; font-size: 12px; color: #666; }
          @media print {
            body { margin: 0; padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            <h2>Electro Diagnostic Center</h2>
            <p>Address Line 1, Street name, Opposite to, City, 123456</p>
            <p>+91 9874563210 | electropath@gmail.com | www.pathoprobe.com</p>
          </div>
          <div class="header-right">
            <div class="qr-code"></div>
            <p style="font-size: 12px; margin-top: 5px;">Scan to download</p>
          </div>
        </div>

        <div class="patient-info">
          <div class="patient-left">
            <p><strong>Mr. ${report.patient?.name || ''}</strong></p>
            <p>Age / Sex: ${report.patient?.dob ? `${getAge(report.patient.dob)} YRS` : ''} / ${report.patient?.gender?.[0] || ''}</p>
            <p>Referred by: ${report.referringDoctor || 'N/A'}</p>
            <p>Reg. no.: ${report.registrationNumber || report.id}</p>
            <p>Collected at: ${report.collectionAddress || report.patient?.address || 'N/A'}</p>
          </div>
          <div class="patient-right">
            <p>Registered on: ${report.registeredDate ? formatDate(report.registeredDate) : formatDate(report.date)}</p>
            <p>Collected on: ${report.collectedDate ? formatDate(report.collectedDate) : formatDate(report.date)}</p>
            <p>Received on: ${report.receivedDate ? formatDate(report.receivedDate) : formatDate(report.date)}</p>
            <p>Reported on: ${report.reportedDate ? formatDate(report.reportedDate) : formatDate(report.date)}</p>
          </div>
        </div>
    `;

    // Add test results by category
    Object.entries(groupedResults).forEach(([category, results]) => {
      html += `
        <div class="test-category">
          <h3>${category}</h3>
          <table>
            <thead>
              <tr>
                <th>TEST</th>
                <th>VALUE</th>
                <th>UNIT</th>
                <th>REFERENCE</th>
              </tr>
            </thead>
            <tbody>
      `;
      
      results.forEach((result) => {
        const abnormal = isAbnormal(result.value, result.test?.referenceRange);
        const abnormalClass = abnormal === 'L' ? 'abnormal' : abnormal === 'H' ? 'abnormal-high' : '';
        html += `
          <tr>
            <td class="${abnormalClass}">${result.test?.name} ${abnormal ? `<strong>${abnormal}</strong>` : ''}</td>
            <td class="${abnormalClass}">${result.value}</td>
            <td>${result.test?.unit || ''}</td>
            <td>${result.test?.referenceRange || ''}</td>
          </tr>
        `;
      });
      
      html += `
            </tbody>
          </table>
        </div>
      `;
    });

    // Add clinical notes, possible causes, and interpretation
    if (report.clinicalNotes) {
      html += `
        <div class="clinical-notes">
          <h4>Clinical Notes:</h4>
          <p>${report.clinicalNotes.replace(/\n/g, '<br>')}</p>
        </div>
      `;
    }

    if (report.possibleCauses) {
      html += `
        <div class="possible-causes">
          <h4>Possible causes of abnormal parameters:</h4>
          <p>${report.possibleCauses.replace(/\n/g, '<br>')}</p>
        </div>
      `;
    }

    if (report.interpretation) {
      html += `
        <div class="interpretation">
          <h4>Interpretation:</h4>
          <p>${report.interpretation.replace(/\n/g, '<br>')}</p>
        </div>
      `;
    }

    // Add footer
    html += `
        <div class="footer">
          <div class="footer-left">
            <p><strong>${report.user?.name || 'Lab Incharge'}</strong></p>
            ${report.user?.designation ? `<p>${report.user.designation}</p>` : ''}
            ${report.user?.education ? `<p>${report.user.education}</p>` : ''}
          </div>
          <div class="footer-right">
            <p><strong>${report.pathologist?.name || 'Pathologist'}</strong></p>
            ${report.pathologist?.designation ? `<p>${report.pathologist.designation}</p>` : ''}
            ${report.pathologist?.education ? `<p>${report.pathologist.education}</p>` : ''}
            ${report.pathologist?.licenseNumber ? `<p>License No.: ${report.pathologist.licenseNumber}</p>` : ''}
          </div>
        </div>

        <div class="disclaimer">
          <p><strong>NOT VALID FOR MEDICO LEGAL PURPOSE</strong></p>
          <p>Work timings: Monday to Saturday, 10 am to 5 pm</p>
          <p>This report was created in the Labsmart software which has inbuilt interpretations. All interpretations are copyright of Labsmart</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!report) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning">Report not found</Alert>
      </Container>
    );
  }

  const reportContent = (
      <Paper sx={{ p: 4, boxShadow: 6 }} id="printable-report">
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider', pb: 2, mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Electro Diagnostic Center</Typography>
            <Typography variant="body2">Address Line 1, Street name, Opposite to, City, 123456</Typography>
            <Typography variant="body2">+91 9874563210 | electropath@gmail.com | www.pathoprobe.com</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <QRCodeSVG value={report.barcode || String(report.id)} size={64} />
            <Typography variant="caption" display="block">Scan to download</Typography>
          </Box>
        </Box>

        {/* Patient & Report Details */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={8}>
            <Typography variant="subtitle2">Mr. {report.patient?.name || ''}</Typography>
            <Typography variant="body2">Age / Sex: {report.patient?.dob ? `${getAge(report.patient.dob)} YRS` : ''} / {report.patient?.gender?.[0]}</Typography>
            <Typography variant="body2">Referred by: {report.referringDoctor || 'N/A'}</Typography>
            <Typography variant="body2">Reg. no.: {report.registrationNumber || report.id}</Typography>
            <Typography variant="body2">Collected at: {report.collectionAddress || report.patient?.address || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2">Registered on: {report.registeredDate ? formatDate(report.registeredDate) : formatDate(report.date)}</Typography>
            <Typography variant="body2">Collected on: {report.collectedDate ? formatDate(report.collectedDate) : formatDate(report.date)}</Typography>
            <Typography variant="body2">Received on: {report.receivedDate ? formatDate(report.receivedDate) : formatDate(report.date)}</Typography>
            <Typography variant="body2">Reported on: {report.reportedDate ? formatDate(report.reportedDate) : formatDate(report.date)}</Typography>
          </Grid>
        </Grid>

        {/* Test Results by Category */}
        {Object.entries(groupedResults).map(([category, results]) => (
          <Box key={category} sx={{ mt: 2, mb: 1 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>{category}</Typography>
                  <TableContainer>
              <Table size="small">
                      <TableHead>
                        <TableRow>
                    <TableCell>TEST</TableCell>
                    <TableCell>VALUE</TableCell>
                    <TableCell>UNIT</TableCell>
                    <TableCell>REFERENCE</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                  {results.map((result) => {
                    const abnormal = isAbnormal(result.value, result.test?.referenceRange);
                    return (
                          <TableRow key={result.id}>
                        <TableCell sx={{ fontWeight: abnormal ? 'bold' : 'normal', color: abnormal === 'L' ? 'red' : abnormal === 'H' ? 'orange' : 'inherit' }}>
                          {result.test?.name} {abnormal ? <b>{abnormal}</b> : ''}
                            </TableCell>
                        <TableCell sx={{ color: abnormal === 'L' ? 'red' : abnormal === 'H' ? 'orange' : 'inherit' }}>{result.value}</TableCell>
                        <TableCell>{result.test?.unit}</TableCell>
                        <TableCell>{result.test?.referenceRange}</TableCell>
                          </TableRow>
                    );
                  })}
                      </TableBody>
                    </Table>
                  </TableContainer>
          </Box>
        ))}

        {/* Clinical Notes */}
        {report.clinicalNotes && (
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Clinical Notes:</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{report.clinicalNotes}</Typography>
          </Box>
        )}

        {/* Possible Causes */}
        {report.possibleCauses && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Possible causes of abnormal parameters:</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{report.possibleCauses}</Typography>
          </Box>
        )}

        {/* Interpretation */}
        {report.interpretation && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Interpretation:</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{report.interpretation}</Typography>
          </Box>
        )}

        {/* Footer: Lab Incharge and Pathologist */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 4, borderTop: 1, borderColor: 'divider', pt: 2 }}>
          <Box>
            <Typography variant="body2">{report.user?.name || 'Lab Incharge'}</Typography>
            {report.user?.designation && (
              <Typography variant="caption" display="block">{report.user.designation}</Typography>
            )}
            {report.user?.education && (
              <Typography variant="caption" display="block">{report.user.education}</Typography>
            )}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2">{report.pathologist?.name || 'Pathologist'}</Typography>
            {report.pathologist?.designation && (
              <Typography variant="caption" display="block">{report.pathologist.designation}</Typography>
            )}
            {report.pathologist?.education && (
              <Typography variant="caption" display="block">{report.pathologist.education}</Typography>
            )}
            {report.pathologist?.licenseNumber && (
              <Typography variant="caption" display="block">License No.: {report.pathologist.licenseNumber}</Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>NOT VALID FOR MEDICO LEGAL PURPOSE</Typography>
          <Typography variant="caption" display="block">Work timings: Monday to Saturday, 10 am to 5 pm</Typography>
          <Typography variant="caption" display="block">This report was created in the Labsmart software which has inbuilt interpretations. All interpretations are copyright of Labsmart</Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<Print />}
        onClick={handlePrint}
          sx={{ mt: 3, display: 'block', mx: 'auto', '@media print': { display: 'none' } }}
        >
          Print Report
        </Button>
      </Paper>
  );

  // If it's a dialog, render as dialog content
  if (isDialog) {
    return (
      <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Report #{report.id}</Typography>
            <Button onClick={onClose} startIcon={<Close />}>
              Close
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            {reportContent}
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Otherwise render as full page
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          Report #{report.id}
        </Typography>
      </Box>
      
      {reportContent}
      
      {/* Print-specific CSS */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #printable-report, #printable-report * { visibility: visible; }
          #printable-report { position: absolute; left: 0; top: 0; width: 100vw; background: white; box-shadow: none; }
          button, [class*='MuiButton-root'] { display: none !important; }
        }
      `}</style>
    </Container>
  );
};

export default ReportView; 